/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { Mail, Send } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import DOMPurify from 'dompurify';
import PostalMime from 'postal-mime';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getAllListData } from 'services/CommonServices';
import { MASTER_ENDPOINT } from 'utils/Constant';

const EmlViewerFromApi = ({ jobId, setEmailDataContent, source }: any) => {
  const [emailData, setEmailData] = useState<{
    from?: string;
    to?: string;
    subject?: string;
    body?: string;
  }>({});

  const [loading, setLoading] = useState(true);
  const param = useParams();
  const fetchEml = async () => {
    try {
      setLoading(true);
      const idToUse = jobId || param.jobId;

      if (!idToUse) {
        setLoading(false);
        return;
      }

      const url = `${MASTER_ENDPOINT.EmlFiles}/${idToUse}`;
      const response = await getAllListData(url);

      let emlText = '';

      if (response instanceof Response) emlText = await response.text();
      else if (typeof response === 'string') emlText = response;
      else return;

      const parsed: any = await PostalMime.parse(emlText);

      const emailBody = parsed?.html || parsed?.text || '';
      if (!emailBody) {
        // console.warn('[EmlViewer] fetchEml - WARNING: parsed email body is EMPTY');
      }

      setEmailData({
        body: emailBody,
        from: parsed?.from?.address || parsed?.from?.name || '',
        subject: parsed?.subject || '',
        to: Array.isArray(parsed?.to)
          ? parsed.to
              .map((r: any) => r.address || r.name)
              .filter(Boolean)
              .join(', ')
          : parsed?.to?.address || parsed?.to?.name || '',
      });
      setEmailDataContent(emailBody);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId && source === 'email') {
      fetchEml();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);
  useEffect(() => {
    const handler = (event: any) => {
      if (event.data?.type === 'iframe-height') {
        const iframe = document.getElementById(
          'emailFrame',
        ) as HTMLIFrameElement;
        if (iframe) {
          iframe.style.height = event.data.height + 'px';
        }
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);
  return (
    <Box sx={{ bgcolor: '#fff', width: '100%' }}>
      {loading ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '60vh',
            justifyContent: 'center',
          }}>
          <CircularProgress sx={{ color: '#be0000' }} />
        </Box>
      ) : emailData.body ? (
        <Box sx={{ mx: 'auto', width: '100%' }}>
          <Paper
            elevation={0}
            sx={{
              border: '1px solid #dcdcdc',
              overflow: 'hidden',
            }}>
            <Box
              sx={{
                borderBottom: '1px solid #ececec',
                px: 3,
                py: 2,
              }}>
              {emailData.from && (
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                  <Mail sx={{ color: '#bf0000', fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 600 }}>From:</Typography>
                  <Typography sx={{ color: '#333', wordBreak: 'break-word' }}>
                    {emailData.from}
                  </Typography>
                </Box>
              )}
              {emailData.to && (
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 1,
                    mt: 1,
                  }}>
                  <Send sx={{ color: '#3f51b5', fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 600 }}>To:</Typography>
                  <Typography sx={{ color: '#333', wordBreak: 'break-word' }}>
                    {emailData.to}
                  </Typography>
                </Box>
              )}{' '}
              <Divider />
              {emailData.subject && (
                <Box
                  sx={{ alignItems: 'center', display: 'flex', gap: 1, mt: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>Subject:</Typography>
                  <Typography
                    sx={{
                      color: '#333',
                      fontWeight: 500,
                      wordBreak: 'break-word',
                    }}>
                    {emailData.subject}
                  </Typography>
                </Box>
              )}
            </Box>
            <Divider />
            <div
              style={{ padding: '20px' }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(emailData.body || '', {
                  FORBID_ATTR: ['http-equiv'],
                  FORBID_TAGS: ['meta'],
                }),
              }}
            />
          </Paper>
        </Box>
      ) : (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '70vh',
            justifyContent: 'center',
          }}>
          <Typography>No email data available.</Typography>
        </Box>
      )}
    </Box>
  );
};

export default EmlViewerFromApi;
